import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Google Identity Services wrapper.
 *
 * index.html loads https://accounts.google.com/gsi/client with async defer, so
 * `window.google` appears at some unknown point after the React tree mounts.
 * Everything here is built around that: poll for the global, then initialize
 * once, then render the button into whatever div the login screen has mounted.
 */

// ---------------------------------------------------------------------------
// Ambient typings for the GSI global. Only the members this app calls are
// declared, on purpose: a hand written subset that fails to compile when we
// reach for something undeclared beats `any` and beats a dependency on
// @types/google.accounts that nothing else in the repo needs.
// ---------------------------------------------------------------------------

interface GoogleCredentialResponse {
  /** The id_token (a JWT). Absent if the user aborted the flow. */
  credential?: string;
  select_by?: string;
}

interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  itp_support?: boolean;
}

interface GoogleButtonConfiguration {
  type: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  locale?: string;
}

interface GoogleAccountsId {
  initialize(config: GoogleIdConfiguration): void;
  renderButton(parent: HTMLElement, options: GoogleButtonConfiguration): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Identity shown in the header. Cosmetic, see decodeIdTokenPayload. */
export interface RescueUser {
  email: string;
  name: string;
  picture?: string;
  /** Workspace hosted domain. Absent for consumer Google accounts. */
  hd?: string;
}

export type AuthStatus =
  /** Waiting for the GSI script. */
  | 'loading'
  /** Ready for a sign in. */
  | 'signed_out'
  /** We hold a credential. */
  | 'signed_in'
  /** Sign in cannot work at all, see blockedReason. */
  | 'unavailable';

export interface GoogleAuth {
  status: AuthStatus;
  user: RescueUser | null;
  /** Raw id_token to send as the Bearer token. Null unless status is signed_in. */
  credential: string | null;
  /** Set when status is 'unavailable': a misconfiguration or a load failure. */
  blockedReason: string | null;
  /** Why the previous session ended, e.g. an expired token. Shown on the login screen. */
  notice: string | null;
  /** Attach to the div that should hold the Google button. */
  buttonRef: RefObject<HTMLDivElement>;
  /** True once Google's button has actually rendered, so the overlay is clickable. */
  buttonReady: boolean;
  signOut: (notice?: string) => void;
}

// How long to wait for accounts.google.com/gsi/client before giving up. Generous,
// because a slow hotel wifi is exactly the situation this tool gets used in.
const GSI_LOAD_TIMEOUT_MS = 15_000;
const GSI_POLL_INTERVAL_MS = 100;

/**
 * Width of Google's hidden button, in px. LoginScreen sizes our visible button to
 * match, because the invisible one is the real click target and a mismatch would
 * leave a strip that looks clickable and is not.
 */
export const GSI_BUTTON_WIDTH = 280;

// Drop the session slightly before the token's own expiry, so a hold that starts
// at the last second still reaches the Function with a valid token instead of
// bouncing off a 401.
const EXPIRY_SAFETY_MARGIN_MS = 60_000;

const SESSION_EXPIRED_NOTICE =
  'Tu sesión de Google expiró. Iniciá sesión de nuevo para llamar al ingeniero de guardia.';

interface IdTokenPayload {
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  hd?: string;
  exp?: number;
}

/**
 * Reads the JWT payload WITHOUT verifying anything.
 *
 * This decode is cosmetic. It exists only so the header can show a name, an email
 * and an avatar, and so we can expire the session before the token does. The
 * Pages Function performs the real verification: RS256 signature against Google's
 * JWKS, `aud`, `iss`, `exp`, `email_verified` and the domain allowlist. Nothing
 * this function returns is ever used to authorize anything.
 */
function decodeIdTokenPayload(token: string): IdTokenPayload | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = (4 - (base64.length % 4)) % 4;
    const binary = atob(base64 + '='.repeat(padding));
    // atob hands back one char per byte (latin1). Percent escaping and decoding
    // recovers the UTF-8 sequence, otherwise a name with an accent renders as
    // mojibake in the header.
    const json = decodeURIComponent(
      Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    const parsed: unknown = JSON.parse(json);
    if (parsed === null || typeof parsed !== 'object') return null;
    return parsed as IdTokenPayload;
  } catch {
    return null;
  }
}

function userFromPayload(payload: IdTokenPayload): RescueUser | null {
  if (typeof payload.email !== 'string' || payload.email.length === 0) return null;
  return {
    email: payload.email,
    // Some accounts have no name claim, the email is a better label than "".
    name: typeof payload.name === 'string' && payload.name.length > 0 ? payload.name : payload.email,
    picture: typeof payload.picture === 'string' ? payload.picture : undefined,
    hd: typeof payload.hd === 'string' ? payload.hd : undefined,
  };
}

interface Session {
  user: RescueUser;
  credential: string;
}

/**
 * Where the credential survives a page reload.
 *
 * It used to live in React state and nowhere else, on the reasoning that a token
 * which pages a human should never sit in storage on a shared restaurant machine.
 * That was the wrong threat model: the people using this are the sales team on
 * their own laptops, and the cost was that a refresh signed you out. Getting
 * logged out mid-incident, at the one moment the page matters, is a worse failure
 * than the storage risk.
 *
 * sessionStorage rather than localStorage on purpose: it is scoped to the tab and
 * dies when the tab does, so a closed laptop lid does not leave a usable token
 * behind for the next person. It also does not meaningfully widen the XSS surface,
 * since script running on this origin could already read the in-memory credential
 * or just call /api/panic itself.
 *
 * Everything read back out is re-validated the same way a fresh credential is:
 * shape, expiry, and the client id it was minted for. Nothing is trusted because
 * it came from storage.
 */
export const SESSION_STORAGE_KEY = 'vertex-rescue.session';

interface StoredSession {
  credential: string;
  /** The client id this token was minted for. A rebuild against a different OAuth client must not restore it: the aud check would 401. */
  clientId: string;
}

export function readStoredCredential(clientId: string): string | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return null;
    const stored = parsed as Partial<StoredSession>;
    if (typeof stored.credential !== 'string' || stored.credential === '') return null;
    if (stored.clientId !== clientId) return null;
    return stored.credential;
  } catch {
    // Storage can throw: disabled cookies, Safari private mode, a quota error, or
    // JSON that is not ours. None of those should break sign in.
    return null;
  }
}

export function writeStoredCredential(credential: string, clientId: string): void {
  try {
    window.sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ credential, clientId } satisfies StoredSession)
    );
  } catch {
    // Not being able to persist is a degraded experience, not a failure. The
    // in-memory session still works for this page view.
  }
}

export function clearStoredCredential(): void {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* see writeStoredCredential */
  }
}

export function useGoogleAuth(): GoogleAuth {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const configured = typeof clientId === 'string' && clientId.trim().length > 0;

  const [scriptReady, setScriptReady] = useState(false);
  // True once Google's real button exists in the DOM. Our own button is only shown
  // then: it is a decoration over their click target, so showing it earlier would
  // give the user a button that looks ready and does nothing.
  const [buttonReady, setButtonReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // React state is the source of truth for the current page view; sessionStorage
  // is only how it survives a reload. See the block above SESSION_STORAGE_KEY for
  // why that tradeoff was made.
  const [session, setSession] = useState<Session | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const buttonRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const renderedIntoRef = useRef<HTMLDivElement | null>(null);
  const expiryTimerRef = useRef<number | null>(null);

  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current !== null) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  const signOut = useCallback(
    (reason?: string) => {
      clearExpiryTimer();
      // Without this, GSI can silently re-select the same account, which is the
      // opposite of what signing out means.
      window.google?.accounts.id.disableAutoSelect();
      renderedIntoRef.current = null;
      setButtonReady(false);
      setSession(null);
      // Must come with the state reset. Leaving the stored credential behind
      // would mean "sign out" lasted exactly until the next refresh.
      clearStoredCredential();
      setNotice(reason ?? null);
    },
    [clearExpiryTimer]
  );

  /**
   * Accepts a credential, from Google or from sessionStorage, and returns whether
   * it was good. `silent` suppresses the user facing notices, because a stale
   * token found in storage on load is not something the user did wrong.
   */
  const acceptCredential = useCallback(
    (credential: string, silent: boolean): boolean => {
      const fail = (notice: string) => {
        if (!silent) setNotice(notice);
        clearStoredCredential();
        return false;
      };

      const payload = decodeIdTokenPayload(credential);
      const user = payload === null ? null : userFromPayload(payload);
      if (payload === null || user === null) {
        return fail('Google devolvió un token que esta aplicación no pudo leer. Probá iniciar sesión de nuevo.');
      }

      const expiresInMs =
        typeof payload.exp === 'number' ? payload.exp * 1000 - Date.now() - EXPIRY_SAFETY_MARGIN_MS : null;
      if (expiresInMs !== null && expiresInMs <= 0) {
        // Clock skew on the client, a token replayed from a stale tab, or the
        // common case: a reload after the token's hour was up.
        return fail(SESSION_EXPIRED_NOTICE);
      }

      clearExpiryTimer();
      if (expiresInMs !== null) {
        expiryTimerRef.current = window.setTimeout(() => {
          expiryTimerRef.current = null;
          renderedIntoRef.current = null;
          setSession(null);
          clearStoredCredential();
          setNotice(SESSION_EXPIRED_NOTICE);
        }, expiresInMs);
      }

      setNotice(null);
      setSession({ user, credential });
      writeStoredCredential(credential, clientId);
      return true;
    },
    [clearExpiryTimer, clientId]
  );

  const handleCredential = useCallback(
    (response: GoogleCredentialResponse) => {
      const credential = response.credential;
      if (typeof credential !== 'string' || credential.length === 0) {
        setNotice('Google no devolvió ninguna credencial. Probá iniciar sesión de nuevo.');
        return;
      }
      acceptCredential(credential, false);
    },
    [acceptCredential]
  );

  // Restore across a reload. Runs once, before Google's script is even needed, so
  // a refresh mid-incident does not drop you back to the sign in screen.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !configured) return;
    restoredRef.current = true;
    const stored = readStoredCredential(clientId);
    if (stored !== null) acceptCredential(stored, true);
  }, [acceptCredential, clientId, configured]);

  // Wait for the async script. Polling beats a load listener because the tag may
  // already have finished before React mounted, in which case no event is coming.
  useEffect(() => {
    if (!configured) return;
    if (window.google !== undefined) {
      setScriptReady(true);
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.google !== undefined) {
        window.clearInterval(timer);
        setScriptReady(true);
        return;
      }
      if (Date.now() - startedAt > GSI_LOAD_TIMEOUT_MS) {
        window.clearInterval(timer);
        setLoadError(
          'El inicio de sesión de Google no cargó. Revisá la conexión de red, o si hay un bloqueador sobre accounts.google.com, y recargá la página.'
        );
      }
    }, GSI_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [configured]);

  useEffect(() => clearExpiryTimer, [clearExpiryTimer]);

  // initialize() is global and idempotent per client id, so it runs once. The ref
  // guard also absorbs StrictMode's double effect invocation in development.
  useEffect(() => {
    if (!scriptReady || !configured || initializedRef.current) return;
    const gsi = window.google;
    if (gsi === undefined) return;

    gsi.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredential,
      // No One Tap and no auto select anywhere in this app. A sales rep on a
      // shared laptop must pick the account deliberately, because the account is
      // the audit trail on the OpsGenie alert.
      auto_select: false,
      itp_support: true,
    });
    initializedRef.current = true;
  }, [clientId, configured, handleCredential, scriptReady]);

  const signedIn = session !== null;


  // Render the button whenever there is a signed out screen holding a fresh div.
  useEffect(() => {
    if (!scriptReady || signedIn) return;
    const container = buttonRef.current;
    const gsi = window.google;
    if (container === null || gsi === undefined) return;
    if (renderedIntoRef.current === container) return;

    container.replaceChildren();
    gsi.accounts.id.renderButton(container, {
      type: 'standard',
      // Invisible, so the theme is irrelevant. Kept as filled_black rather than
      // outline only so a debugging session with opacity turned back up is
      // legible on the dark page.
      theme: 'filled_black',
      size: 'large',
      text: 'signin_with',
      // Rectangular so the hit area fills the frame with no dead corners. Nothing
      // here is seen: LoginScreen lays our own button underneath and renders this
      // one at opacity 0 on top of it, because everything Google draws lives in a
      // cross origin iframe we cannot style. Their white logo chip was the last
      // thing that could not be themed away.
      shape: 'rectangular',
      logo_alignment: 'left',
      width: GSI_BUTTON_WIDTH,
    });
    renderedIntoRef.current = container;
    setButtonReady(true);
  }, [scriptReady, signedIn]);

  let status: AuthStatus;
  let blockedReason: string | null = null;
  if (!configured) {
    status = 'unavailable';
    blockedReason =
      'VITE_GOOGLE_CLIENT_ID no estaba definido cuando se compiló esta versión, así que el inicio de sesión no puede arrancar. Recompilá con el client id de Google OAuth, tiene que coincidir con el GOOGLE_CLIENT_ID que usa la API.';
  } else if (loadError !== null) {
    status = 'unavailable';
    blockedReason = loadError;
  } else if (session !== null) {
    status = 'signed_in';
  } else if (scriptReady) {
    status = 'signed_out';
  } else {
    status = 'loading';
  }

  return {
    status,
    user: session?.user ?? null,
    credential: session?.credential ?? null,
    blockedReason,
    notice,
    buttonRef,
    buttonReady,
    signOut,
  };
}
